package dev.terrakok.mapitan.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.drag
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import dev.terrakok.mapitan.domain.utils.hexRgbToColorOrNull
import dev.terrakok.mapitan.domain.utils.hsvToColor
import dev.terrakok.mapitan.domain.utils.toHSV
import dev.terrakok.mapitan.domain.utils.toHexRgb
import kotlin.math.abs

@Composable
fun ColorPalette(
    color: Color,
    onColorChanged: (Color) -> Unit,
    modifier: Modifier = Modifier,
) {
    val state = remember { ColorPaletteState(color) }
    val focusManager = LocalFocusManager.current
    LaunchedEffect(color) {
        if (!color.approxEquals(state.color)) state.setColor(color)
    }

    Column(
        modifier = modifier.pointerInput(Unit) {
            awaitEachGesture {
                awaitFirstDown()
                focusManager.clearFocus()
            }
        },
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        ColorArea(
            hue = state.hue,
            saturation = state.saturation,
            brightness = state.brightness,
            onChanged = { saturation, brightness ->
                state.updateSaturationBrightness(saturation, brightness)
                onColorChanged(state.color)
            },
            modifier = Modifier
                .fillMaxWidth()
                .aspectRatio(1.5f),
        )

        ColorBar(
            gradient = remember { Brush.horizontalGradient(hueGradientColors) },
            position = state.hue / 360f,
            onPositionChanged = { position ->
                state.updateHue(position * 360f)
                onColorChanged(state.color)
            },
            modifier = Modifier
                .fillMaxWidth()
                .height(32.dp),
        )

        ColorBar(
            gradient = remember(state.hue, state.saturation) {
                Brush.horizontalGradient(
                    listOf(Color.Black, hsvToColor(state.hue, state.saturation, 1f)),
                )
            },
            position = state.brightness,
            onPositionChanged = { position ->
                state.updateBrightness(position)
                onColorChanged(state.color)
            },
            modifier = Modifier
                .fillMaxWidth()
                .height(32.dp),
        )

        HexInput(
            color = state.color,
            onColorChanged = { newColor ->
                state.setColor(newColor)
                onColorChanged(state.color)
            },
        )
    }
}

@Stable
private class ColorPaletteState(initialColor: Color) {
    var hue by mutableFloatStateOf(0f)
        private set

    var saturation by mutableFloatStateOf(0f)
        private set

    var brightness by mutableFloatStateOf(0f)
        private set

    val color: Color get() = hsvToColor(hue, saturation, brightness)

    init {
        setColor(initialColor)
    }

    fun updateHue(degrees: Float) {
        hue = degrees.coerceIn(0f, 360f) % 360f
    }

    fun updateSaturationBrightness(saturation: Float, brightness: Float) {
        this.saturation = saturation.coerceIn(0f, 1f)
        this.brightness = brightness.coerceIn(0f, 1f)
    }

    fun updateBrightness(brightness: Float) {
        this.brightness = brightness.coerceIn(0f, 1f)
    }

    fun setColor(color: Color) {
        val (h, s, v) = color.toHSV()
        if (v > 0f && s > 0f) hue = h
        if (v > 0f) saturation = s
        brightness = v
    }
}

@Composable
private fun ColorArea(
    hue: Float,
    saturation: Float,
    brightness: Float,
    onChanged: (saturation: Float, brightness: Float) -> Unit,
    modifier: Modifier = Modifier,
) {
    val hueColor = remember(hue) { hsvToColor(hue, 1f, 1f) }

    Canvas(
        modifier = modifier
            .clip(RoundedCornerShape(16.dp))
            .pointerPosition { position, size ->
                onChanged(
                    position.x / size.width,
                    1f - position.y / size.height,
                )
            },
    ) {
        drawRect(brush = Brush.horizontalGradient(listOf(Color.White, hueColor)))
        drawRect(brush = Brush.verticalGradient(listOf(Color.Transparent, Color.Black)))
        drawThumb(
            center = Offset(
                x = saturation * size.width,
                y = (1f - brightness) * size.height,
            ),
        )
    }
}

@Composable
private fun ColorBar(
    gradient: Brush,
    position: Float,
    onPositionChanged: (Float) -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(gradient)
            .pointerPosition { offset, size ->
                onPositionChanged(offset.x / size.width)
            },
        contentAlignment = Alignment.CenterStart,
    ) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            drawThumb(
                center = Offset(
                    x = position * size.width,
                    y = size.height / 2f,
                ),
            )
        }
    }
}

@Composable
private fun Modifier.pointerPosition(
    onPosition: (position: Offset, size: IntSize) -> Unit,
): Modifier {
    val callback by rememberUpdatedState(onPosition)
    val focusManager = LocalFocusManager.current
    return this.pointerInput(Unit) {
        awaitEachGesture {
            val down = awaitFirstDown(requireUnconsumed = false)
            down.consume()
            focusManager.clearFocus()
            callback(down.position, size)
            drag(down.id) { change ->
                change.consume()
                callback(change.position, size)
            }
        }
    }
}

private fun DrawScope.drawThumb(center: Offset) {
    val radius = 9.dp.toPx()
    val strokeWidth = 2.dp.toPx()
    val clampedCenter = Offset(
        x = center.x.coerceIn(0f, size.width),
        y = center.y.coerceIn(0f, size.height),
    )
    drawCircle(
        color = Color.Black.copy(alpha = 0.4f),
        radius = radius + strokeWidth / 2f,
        center = clampedCenter,
        style = Stroke(width = strokeWidth),
    )
    drawCircle(
        color = Color.White,
        radius = radius,
        center = clampedCenter,
        style = Stroke(width = strokeWidth),
    )
}

@Composable
private fun HexInput(
    color: Color,
    onColorChanged: (Color) -> Unit,
    modifier: Modifier = Modifier,
) {
    val hex = color.toHexRgb().removePrefix("#")
    val focusManager = LocalFocusManager.current
    var fieldValue by remember { mutableStateOf(TextFieldValue(hex, TextRange(hex.length))) }
    var isFocused by remember { mutableStateOf(false) }

    LaunchedEffect(hex, isFocused) {
        if (!isFocused && !fieldValue.text.equals(hex, ignoreCase = true)) {
            fieldValue = TextFieldValue(hex, TextRange(hex.length))
        }
    }

    Row(
        modifier = modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Text(
            modifier = Modifier
                .padding(horizontal = 12.dp, vertical = 8.dp),
            text = "HEX",
            style = MaterialTheme.typography.bodyMedium.copy(
                fontWeight = FontWeight.Medium,
            ),
        )

        Box(
            modifier = Modifier
                .weight(1f)
                .clip(RoundedCornerShape(12.dp))
                .background(MaterialTheme.colorScheme.surfaceContainerHighest)
                .padding(horizontal = 12.dp, vertical = 4.dp),
        ) {
            BasicTextField(
                value = fieldValue,
                onValueChange = { newValue ->
                    val edited = fieldValue.editHex(newValue)
                    fieldValue = edited
                    if (!edited.text.equals(hex, ignoreCase = true)) {
                        edited.text.hexRgbToColorOrNull()?.let(onColorChanged)
                    }
                },
                singleLine = true,
                keyboardActions = KeyboardActions(onDone = { focusManager.clearFocus() }),
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Ascii,
                    capitalization = KeyboardCapitalization.Characters,
                    autoCorrectEnabled = false,
                    imeAction = ImeAction.Done,
                ),
                textStyle = TextStyle(
                    fontSize = 24.sp,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface,
                    textAlign = TextAlign.Start,
                ),
                modifier = Modifier
                    .fillMaxWidth()
                    .onFocusChanged { focusState ->
                        if (isFocused && !focusState.isFocused) {
                            val completed = fieldValue.text.completeHex(hex)
                            fieldValue = TextFieldValue(completed, TextRange(completed.length))
                            completed.hexRgbToColorOrNull()?.let(onColorChanged)
                        }
                        isFocused = focusState.isFocused
                    },
                decorationBox = { innerTextField ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = "#",
                            style = TextStyle(
                                fontSize = 24.sp,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            ),
                            modifier = Modifier.padding(end = 4.dp),
                        )
                        innerTextField()
                    }
                },
            )
        }
    }
}

private const val HEX_LENGTH = 6

private fun TextFieldValue.editHex(newValue: TextFieldValue): TextFieldValue {
    if (newValue.text == text) {
        return TextFieldValue(
            text = text,
            selection = TextRange(
                newValue.selection.start.coerceIn(0, text.length),
                newValue.selection.end.coerceIn(0, text.length),
            ),
        )
    }

    val selectionLength = selection.max - selection.min
    val typedLength = newValue.text.length - (text.length - selectionLength)

    if (typedLength <= 0) {
        val cleaned = newValue.text.filter { it.isHexDigit() }.take(HEX_LENGTH).uppercase()
        val caret = newValue.selection.start.coerceIn(0, cleaned.length)
        return TextFieldValue(cleaned, TextRange(caret))
    }

    val builder = StringBuilder(
        if (selection.collapsed) text else text.removeRange(selection.min, selection.max),
    )
    val insertAt = if (selection.collapsed) {
        (newValue.selection.start - typedLength).coerceIn(0, builder.length)
    } else {
        selection.min.coerceIn(0, builder.length)
    }
    val typed = newValue.text
        .substring(
            startIndex = (newValue.selection.start - typedLength).coerceIn(0, newValue.text.length),
            endIndex = newValue.selection.start.coerceIn(0, newValue.text.length),
        )
        .filter { it.isHexDigit() }
        .uppercase()

    if (builder.length < HEX_LENGTH) {
        builder.insert(insertAt, typed)
        if (builder.length > HEX_LENGTH) builder.setLength(HEX_LENGTH)
        val caret = (insertAt + typed.length).coerceAtMost(HEX_LENGTH)
        return TextFieldValue(builder.toString(), TextRange(caret))
    }

    var caret = insertAt
    typed.forEach { char ->
        if (caret >= HEX_LENGTH) return@forEach
        builder[caret] = char
        caret++
    }
    return TextFieldValue(builder.toString(), TextRange(caret))
}

private fun String.completeHex(fallback: String): String = when {
    isEmpty() -> fallback
    length == HEX_LENGTH -> this
    length == 3 -> map { "$it$it" }.joinToString(separator = "")
    else -> padEnd(HEX_LENGTH, '0')
}

private val hueGradientColors = listOf(
    Color(0xFFFF0000),
    Color(0xFFFFFF00),
    Color(0xFF00FF00),
    Color(0xFF00FFFF),
    Color(0xFF0000FF),
    Color(0xFFFF00FF),
    Color(0xFFFF0000),
)

private fun Char.isHexDigit(): Boolean =
    this in '0'..'9' || this in 'a'..'f' || this in 'A'..'F'

private fun Color.approxEquals(other: Color, tolerance: Float = 1f / 255f): Boolean =
    abs(red - other.red) <= tolerance &&
        abs(green - other.green) <= tolerance &&
        abs(blue - other.blue) <= tolerance

@Preview(showBackground = true)
@Composable
private fun ColorPalettePreview() {
    var previewColor by remember { mutableStateOf(Color(0xFF804043)) }
    Box(modifier = Modifier.padding(16.dp)) {
        ColorPalette(
            color = previewColor,
            onColorChanged = { previewColor = it },
        )
    }
}
